<template>
  <base-section
    id="blog"
    space="72"
  >
    <v-container style="max-width: 1160px">
      <base-section-heading
        :title="$t('blog.heading')"
        :subtitle="$t('blog.subheading')"
        :is-uppercase="false"
        title-tag="h2"
        :space="6"
      />
      <v-row>
        <v-col
          v-if="!loading && posts.length === 0"
          cols="12"
        >
          <base-body class="text-center">
            No articles available.
          </base-body>
        </v-col>
        <v-col
          v-for="(post, index) in posts"
          :key="index"
          cols="12"
          md="4"
        >
          <component
            :is="post.id ? 'router-link' : 'div'"
            :to="post.id ? { name: 'BlogArticle', params: { lang: $i18n.locale, slug: post.slug || post.id } } : undefined"
            class="blog-card-link"
          >
            <v-card
              class="blog-card"
              outlined
            >
              <v-img
                :alt="post.title || 'blog post'"
                :src="post.image"
                height="200"
              />
              <v-card-text class="blog-card-text">
                <div class="blog-meta">
                  {{ post.date }} · {{ post.category }}
                </div>
                <div class="blog-title">
                  {{ post.title }}
                </div>
                <div class="blog-desc">
                  {{ post.excerpt }}
                </div>
                <div
                  v-if="post.tags"
                  class="blog-tags"
                >
                  {{ post.tags }}
                </div>
              </v-card-text>
            </v-card>
          </component>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-col
          cols="12"
          md="6"
        >
          <v-pagination
            v-if="totalPages > 1"
            v-model="page"
            :length="totalPages"
            total-visible="7"
            @input="loadPosts"
          />
        </v-col>
      </v-row>
    </v-container>
  </base-section>
</template>

<script>
  export default {
    name: 'SectionBlogList',

    metaInfo() {
      const origin = typeof window !== 'undefined' ? window.location.origin : ''
      const items = (this.posts || []).map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.title || '',
        url: `${origin}/${this.$i18n.locale}/blog/${item.slug || item.id}`,
        image: item.image || '',
      }))
      return {
        script: [
          {
            vmid: 'blog-list-jsonld',
            type: 'application/ld+json',
            json: {
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: items,
            },
          },
        ],
      }
    },

    data: () => ({
      loading: false,
      page: 1,
      size: 6,
      total: 0,
      posts: [],
      categoryMap: {},
      tagMap: {},
    }),

    computed: {
      totalPages() {
        return Math.max(1, Math.ceil(this.total / this.size))
      },
    },

    watch: {
      '$i18n.locale'() {
        this.page = 1
        this.loadTaxonomies()
        this.loadPosts()
      },
      '$route.fullPath'() {
        this.page = 1
        this.loadPosts()
      },
    },

    async mounted() {
      await this.loadTaxonomies()
      await this.loadPosts()
    },

    methods: {
      slugify(text) {
        if (!text) return ''
        return text
          .toString()
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .trim()
          .replace(/\s+/g, '-')
      },
      buildMap(list) {
        if (!Array.isArray(list)) return {}
        return list.reduce((acc, item) => {
          if (item && item.dictValue) {
            acc[item.dictValue] = item.dictLabel
          }
          return acc
        }, {})
      },
      async loadTaxonomies() {
        try {
          const [tagResp, categoryResp] = await Promise.all([
            this.$axios({ method: 'get', url: '/public/dict/data', params: { dictType: 'sys_blog_tag' } }),
            this.$axios({ method: 'get', url: '/public/dict/data', params: { dictType: 'sys_blog_category' } }),
          ])
          const tags = Array.isArray(tagResp) ? tagResp : (tagResp?.data || tagResp?.items || [])
          const categories = Array.isArray(categoryResp) ? categoryResp : (categoryResp?.data || categoryResp?.items || [])
          this.tagMap = this.buildMap(tags)
          this.categoryMap = this.buildMap(categories)
        } catch (error) {
          this.tagMap = {}
          this.categoryMap = {}
        }
      },
      mapCategory(value) {
        if (!value) return 'Blog'
        return this.categoryMap[value] || value
      },
      mapTags(value) {
        if (!value) return []
        const tags = value.split(',').map(v => v.trim()).filter(Boolean)
        return tags.map(tag => this.tagMap[tag] || tag)
      },
      pickLocalizedRows(items) {
        const locale = this.$i18n.locale || 'en'
        const groups = new Map()
        for (const item of (items || [])) {
          if (!item) continue
          const key = item.groupKey || `id-${item.id}`
          const list = groups.get(key) || []
          list.push(item)
          groups.set(key, list)
        }
        const picked = []
        for (const list of groups.values()) {
          const current = list.find(row => row.lang === locale)
          const fallbackEn = list.find(row => row.lang === 'en')
          picked.push(current || fallbackEn || list[0])
        }
        return picked
      },
      async loadPosts() {
        this.loading = true
        try {
          const routeName = this.$route.name
          const category = routeName === 'BlogCategory' ? this.$route.params.slug : undefined
          const tag = routeName === 'BlogTag' ? this.$route.params.slug : undefined
          // 从 URL query 中读取搜索关键词（由 Search.vue 跳转时传入）
          const keyword = this.$route.query.keyword || undefined
          const resp = await this.$axios({
            method: 'get',
            url: '/public/blog',
            params: { lang: this.$i18n.locale, page: this.page, size: this.size, category, tag, keyword },
          })
          const items = (resp && resp.items) ? resp.items : (resp || [])
          const localized = this.pickLocalizedRows(items)
          const displayItems = [...localized]
            .filter(item => item && item.visible !== false)
            .sort((a, b) => (Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)) || (Number(b?.id || 0) - Number(a?.id || 0)))
          this.total = (resp && typeof resp.total === 'number') ? resp.total : displayItems.length
          this.posts = displayItems.map(item => ({
            ...item,
            date: item.publishAt,
            category: this.mapCategory(item.category),
            tags: this.mapTags(item.tags).join(', '),
            excerpt: item.excerpt,
            image: item.coverImage,
            slug: item.slug || String(item.id || ''),
          }))
        } catch (error) {
          this.posts = []
          this.total = 0
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style lang="sass" scoped>
.blog-card
  height: 100%

.blog-card-link
  text-decoration: none
  display: block
  height: 100%
  color: inherit

.blog-card-text
  display: flex
  flex-direction: column
  min-height: 150px

.blog-meta
  color: rgba(0, 0, 0, .7)
  font-size: 13px

.blog-title
  font-size: 18px
  font-weight: 600
  margin-top: 8px
  color: rgba(0,0,0,.9)
  display: -webkit-box
  -webkit-line-clamp: 2
  -webkit-box-orient: vertical
  overflow: hidden

.blog-desc
  margin-top: 10px
  color: rgba(0, 0, 0, .7)
  line-height: 1.6
  display: -webkit-box
  -webkit-line-clamp: 2
  -webkit-box-orient: vertical
  overflow: hidden

.blog-tags
  margin-top: 8px
  color: rgba(0, 0, 0, .7)
  font-size: 12px
</style>
